import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginWithOAuth } from '../store/slices/authSlice';
import api from '../services/api';
import { toast } from 'react-toastify';

// Two-step forgot-password flow: request a 6-digit code by email, then enter the
// code + a new password. A successful reset returns a session token, so we log the
// user straight in (via loginWithOAuth, which sets credentials from token+user).
const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'reset'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Generic response — doesn't reveal whether the email is registered.
      await api.post('/auth/forgot-password', { email: email.trim() });
      setStep('reset');
      toast.success('If an account exists, a reset code is on its way.');
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length !== 6) { setError('Enter the 6-digit code from your email.'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/reset-password', { email: email.trim(), code: code.trim(), newPassword });
      if (data.token) {
        dispatch(loginWithOAuth({ token: data.token, user: data.user }));
        toast.success('Password reset — welcome back!');
        navigate('/');
      } else {
        toast.success('Password reset. Please sign in with your new password.');
        navigate('/login');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="auth-bg-fixed" style={{ backgroundImage: "url('/images/Background.png')" }} />
      <div className="auth-page-wrap">
        <section className="auth-hero">
          <div className="auth-hero-brand">
            <div className="auth-brand-wordmark">
              <span className="auth-brand-wordmark-text"><span className="wm">MyCarCollection</span></span>
            </div>
          </div>

          <div className="auth-hero-card">
            <div className="auth-glass-card">
              <div className="auth-form-header">
                <h2 className="auth-form-title">Reset password</h2>
                <p className="auth-form-sub">
                  {step === 'request'
                    ? 'Enter your email and we’ll send you a 6-digit code.'
                    : `Enter the code sent to ${email.trim()} and choose a new password.`}
                </p>
              </div>

              {step === 'request' ? (
                <form onSubmit={requestCode} className="auth-form">
                  <div className="auth-field">
                    <label className="auth-label">Email</label>
                    <input
                      className="auth-input"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(ev) => { setError(''); setEmail(ev.target.value); }}
                      required
                      autoFocus
                    />
                  </div>
                  {error && <div className="auth-error">{error}</div>}
                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? 'Sending…' : 'Send code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={submitReset} className="auth-form">
                  <div className="auth-field">
                    <label className="auth-label">6-digit code</label>
                    <input
                      className="auth-input"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={code}
                      onChange={(ev) => { setError(''); setCode(ev.target.value.replace(/[^0-9]/g, '')); }}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">New password</label>
                    <input
                      className="auth-input"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(ev) => { setError(''); setNewPassword(ev.target.value); }}
                      required
                    />
                  </div>
                  {error && <div className="auth-error">{error}</div>}
                  <button type="submit" className="auth-submit" disabled={loading}>
                    {loading ? 'Resetting…' : 'Reset password'}
                  </button>
                  <button
                    type="button"
                    onClick={requestCode}
                    disabled={loading}
                    style={{ background: 'none', border: 'none', color: '#8892b0', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline', marginTop: 4 }}
                  >
                    Didn’t get it? Resend code
                  </button>
                </form>
              )}

              <div className="auth-footer-text">
                Remembered it? <Link to="/login">Back to sign in</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ForgotPassword;
